import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CopilotDocument } from '../../_interfaces/document';
import { Dictation } from '../../_interfaces/dictation';

@Injectable({ providedIn: 'root' })
export class DocumentFreshnessService {
  constructor(private readonly translate: TranslateService) {}

  isStale(dictation: Dictation, document: CopilotDocument): boolean {
    const contentUpdatedAt = dictation.copilot?.content_updated_at;
    if (!contentUpdatedAt) {
      return false;
    }

    const sourceContentUpdatedAt = document.source_content_updated_at;
    if (!sourceContentUpdatedAt) {
      return true;
    }

    return contentUpdatedAt > sourceContentUpdatedAt;
  }

  formatUpdatedLabel(document: CopilotDocument | null | undefined, now: Date = new Date()): string {
    const syncedTimestamp = document?.last_synced_at ?? document?.source_content_updated_at;
    const timestamp = syncedTimestamp ?? document?.createdAt;
    if (!timestamp) {
      return '';
    }

    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const label = syncedTimestamp ? 'Synced' : 'Created';
    const today = this.startOfDay(now);
    const target = this.startOfDay(parsed);

    if (target.getTime() === today.getTime()) {
      const time = this.formatClockTime(parsed);
      return this.translate.instant(`${label} {{time}}`, { time });
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.getTime() === yesterday.getTime()) {
      return this.translate.instant(`${label} yesterday`);
    }

    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const date = parsed.getFullYear() < now.getFullYear()
      ? `${dd}/${mm}/${parsed.getFullYear()}`
      : `${dd}/${mm}`;
    return this.translate.instant(`${label} {{date}}`, { date });
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private formatClockTime(date: Date): string {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
}
